import React, { useEffect, useRef, useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { registerObject } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ApiError } from "go-core/api/types";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import {
	ProductApi,
	ProductStateApi,
	StockAdjustmentApi,
	StockAdjustmentItemApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../services/Api/api";
import ChangeProductModal from "../../../../../components/ChangeProductModal";
import StockAdjustmentItemsTableActions from "./StockAdjustmentItemsTableActions";
import StockAdjustmentItemsTableBody from "./StockAdjustmentItemsTableBody";
import StockAdjustmentItemsTableHeader from "./StockAdjustmentItemsTableHeader";

interface Props {
	formErrors: ApiError[];
	form: UseFormReturn<StockAdjustmentApi>;
	fetchStockAdjustment?: () => void;
}

const StockAdjustmentItemsTable = ({ form, formErrors, fetchStockAdjustment }: Props) => {
	const { t } = useTranslation();
	const optionsRef = useRef<{ label: string }[]>([]);
	const { control, register, resetField, watch, setValue } = form;
	const watchedItems = watch("items");
	const { addFlash } = useFlash();
	const watchedProductIds = watchedItems.map((item) => item.product_id).filter((id) => id);
	const { fields, remove, append, replace } = useFieldArray({
		control,
		name: "items",
		keyName: "key",
	});
	const [shownIndexes, setShownIndexes] = useState<number[]>([]);
	const [showChangeProductModal, setShowChangeProductModal] = useState<number | null>(null);
	const [productStates, setProductStates] = useState<ProductStateApi[]>([]);
	const watchedStockId = watch("stock_id");
	const watchedProductIdsAsString = JSON.stringify(watchedProductIds);
	const itemsAsString = JSON.stringify(watchedItems);
	const { isMobile } = useWindowSize();

	useEffect(() => {
		onUpdateFields();
	}, []);

	useEffect(() => {
		onUpdateFields();
	}, [fields]);

	useEffect(() => {
		if (watch("stock_id")) {
			onFetchProductStates();
		}
	}, [watchedStockId, watchedProductIdsAsString]);

	const checkIfAddNewItem = () => {
		const prevItem = watchedItems[watchedItems?.length - 1];
		return prevItem?.product_id || !isNaN(prevItem?.quantity);
	};

	useEffect(() => {
		if (checkIfAddNewItem()) append({ position: watchedItems.length - 1, type: "INCOME" });
	}, [itemsAsString]);

	const onUpdateFields = () => {
		fields.forEach((field, index) => {
			setValue(`items.${index}.position`, index);
			registerObject(register, `items.${index}.product`, ["measure_type_unit", "name", "id", "last_price_money"]);
			setValue(`items.${index}.product`, field?.product);
			setValue(`items.${index}.product_id`, field?.product?.id);
		});
	};

	const onFetchProductStates = async () => {
		try {
			const params = {
				include: "volumes",
				stock_id: watch("stock_id"),
				product_ids: watchedProductIds.join(","),
			};
			const res = await api.organization().getProductState(params);
			setProductStates(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onFetchProductLastPrice = async (item: StockAdjustmentItemApi, index: number) => {
		if (item.product_id) {
			setTimeout(async () => {
				try {
					const res = await api.organization().getProductLastPrice(item?.product_id, item?.volume || 0);
					if (!res) resetField(`items.${index}.price_net_money.amount`);
					else setValue(`items.${index}.price_net_money.amount`, res.amount);
				} catch (err) {
					handleError.alert(err, addFlash);
				}
			}, 10);
		}
	};

	const searchProducts = async (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>,
		index?: number
	) => {
		if (index || index === 0) {
			const itemType = watch(`items.${index}.type`);
			if (itemType === "INCOME") {
				params = {
					...params,
					"productType|e": `BASIC${FILTER_VALUE_SEPARATOR}COMPOSITE`,
				};
			}
		}
		return api.organization().getProductSearchSelect(
			search,
			{
				...params,
				include: "volumes_v2",
			},
			{
				cancelToken: options?.token,
			}
		);
	};

	const onChangeProduct = async (product: ProductApi, index: number) => {
		if (!product) return;

		setValue(`items.${index}.id`, undefined);
		registerObject(register, `items.${index}.product`, ["measure_type_unit", "name", "id", "last_price_money"]);
		setValue(`items.${index}.product`, product);
		setValue(`items.${index}.product_id`, product.id, { shouldDirty: true });
		setValue(`items.${index}.volume`, product?.default_volume ?? null, { shouldDirty: true });
	};
	const onParseProductOptionValue = (option: { label: string }) => {
		optionsRef.current = optionsRef.current.find((o) => o.label === option.label)
			? [...optionsRef.current]
			: [...optionsRef.current, option];
		return option.label;
	};

	const onConfirmChangeProduct = async (product: ProductApi) => {
		if (showChangeProductModal !== null) {
			await onChangeProduct(product, showChangeProductModal);
			setShowChangeProductModal(null);
		}
	};

	return (
		<fieldset className="form-group">
			<div className="d-flex align-items-center justify-content-between">
				<h5 className={isMobile ? "mb-0" : ""}>{t("common.word.items")}</h5>
				<StockAdjustmentItemsTableActions
					form={form}
					remove={remove}
					append={append}
					replace={replace}
					watchedItems={watchedItems}
				/>
			</div>
			<div className={isMobile ? "table-responsive" : ""}>
				<table
					className={`table table-form positions-table positions-table--small ${
						isMobile ? "vertical-top" : ""
					}`}
				>
					<StockAdjustmentItemsTableHeader
						watchedItems={watchedItems}
						shownIndexes={shownIndexes}
						setShownIndexes={setShownIndexes}
						fields={fields}
					/>
					<StockAdjustmentItemsTableBody
						watchedItems={watchedItems}
						fields={fields}
						shownIndexes={shownIndexes}
						form={form}
						formErrors={formErrors}
						setShownIndexes={setShownIndexes}
						onChangeProduct={onChangeProduct}
						onParseProductOptionValue={onParseProductOptionValue}
						remove={remove}
						fetchProductLastPrice={onFetchProductLastPrice}
						setShowChangeProductModal={setShowChangeProductModal}
						productStates={productStates}
						fetchStockAdjustment={fetchStockAdjustment}
						searchProducts={searchProducts}
					/>
				</table>
			</div>
			{showChangeProductModal !== null && (
				<ChangeProductModal
					isShown={showChangeProductModal !== null}
					onHide={() => setShowChangeProductModal(null)}
					onConfirm={onConfirmChangeProduct}
					onSearchProducts={searchProducts}
					index={showChangeProductModal}
					form={form}
					onParseOptionValue={onParseProductOptionValue}
				/>
			)}
		</fieldset>
	);
};

export default StockAdjustmentItemsTable;
